import * as React from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { C_DASHBOARD_TOAST } from "@/constants/customer-portal/dashboard.constant";
import { SOCKET_KEYS } from "@/constants/socket.constant";
import {
  WareHouseConstants,
  WareHouseStatus,
} from "@/constants/ware-housing.constants";
import { useSocket } from "@/hooks/useSocket";
import { SocketData } from "@/interfaces/globals";
import { customerUser } from "@/interfaces/interfaces";
import { IWareHouse } from "@/interfaces/ware-house.interface";
import { C_WareHouseAPI } from "@/redux/api/customer-portal/C_WareHouseQuery";
import { getSubdomain, tableUpdateToast } from "@/utils/helpers";

type WareHouseContextReturnType = {
  wareHouseId: string;
  customerId: string;
  wareHouseDetail: IWareHouse;
  customerName?: string;
  isForCustomer: boolean;
  isArchived: boolean;
};

type WareHouseContextProviderProps = {
  children: React.ReactNode;
};

type WareHousingContextReturnType = {
  WareHouseContextProvider: ({
    children,
  }: WareHouseContextProviderProps) => JSX.Element;
  useWareHouseContext: () => WareHouseContextReturnType;
};

const createWareHousingContext = (): WareHousingContextReturnType => {
  const WareHouseContext = React.createContext<
    WareHouseContextReturnType | undefined
  >(undefined);

  const WareHouseContextProvider = ({
    children,
  }: WareHouseContextProviderProps): JSX.Element => {
    const { id: wareHouseId } = useParams();
    const subdomain = getSubdomain() || window?.location?.host?.split(".")[0];

    const isForCustomer = subdomain === "customer";

    const data: IWareHouse = JSON.parse(
      localStorage.getItem(WareHouseConstants.KEYS.WAREHOUSE_DETAILS)!,
    );

    const customerData: customerUser = JSON.parse(
      localStorage.getItem("customer-data")!,
    );
    const { socket } = useSocket("/");
    const dispatch = useDispatch();

    React.useEffect(() => {
      socket?.on(SOCKET_KEYS.WAREHOUSE_PROJECTS, (data: SocketData) => {
        dispatch(
          C_WareHouseAPI.util.invalidateTags([
            "warehouseAssignHistory",
            "warehouseAssignHistory",
            "warehouseItems",
          ]),
        );
        tableUpdateToast(data, C_DASHBOARD_TOAST.WAREHOUSE_UPDATE_SUCCESS);
      });

      return (): void => {
        socket?.off(SOCKET_KEYS.WAREHOUSE_PROJECTS);
      };
    }, [socket]);

    return (
      <WareHouseContext.Provider
        value={{
          wareHouseId: isForCustomer ? (customerData?.id ?? "") : wareHouseId!,
          customerId: data?.customerId,
          wareHouseDetail: data,
          customerName: isForCustomer
            ? customerData?.name
            : data?.customer?.name,
          isForCustomer,
          isArchived: data?.status === WareHouseStatus.ARCHIVE,
        }}
      >
        {children}
      </WareHouseContext.Provider>
    );
  };

  const useWareHouseContext = (): WareHouseContextReturnType => {
    const context = React.useContext(WareHouseContext);
    if (context === undefined) {
      throw new Error(
        "useWareHouseContext must be used within a WareHouseContextProvider",
      );
    }

    return context;
  };

  return { WareHouseContextProvider, useWareHouseContext };
};

const { WareHouseContextProvider, useWareHouseContext } =
  createWareHousingContext();

export {
  createWareHousingContext,
  WareHouseContextProvider,
  useWareHouseContext,
};
